import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { CollapsibleList } from 'ui-components/components/collapsible-list';
import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';

import MappingsDropDown from './mappings-dropdown';
import messages from './messages';
import style from './style.module.less';
import { emptyArray } from 'ui-components/default-props/empty';
import { Workflows } from '../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';

const getFieldName = (name) => {
  const msg = messages[name];
  return msg ? <FormattedMessage {...msg} /> : name;
};

const OrderImportColumnMapper = ({
  importedCols = emptyArray,
  shipStationColsGroupings = emptyArray,
  getMatchingCSVField,
  setMatchingCSVField,
  expandKey,
  setExpandKey,
}) => {
  const intl = useIntl();
  const trackOrderImport = useTrackWorkflow(Workflows.ORDER_IMPORT_FLOW);

  const trackClick = (field) => {
    trackOrderImport(WorkflowSteps.ORDER_IMPORT_MAPPING_CLICK, { field });
  };

  const displayCols = useMemo(() => {
    const getDisplayName = (name) => {
      const msg = messages[name];
      return msg ? intl.formatMessage(msg) : name;
    };
    return importedCols.map((col) => {
      const { name } = col;
      return {
        name,
        displayName: getDisplayName(name),
      };
    });
  }, [importedCols, intl]);

  return (
    <CollapsibleList>
      {shipStationColsGroupings.map(({ fieldTitle, cols, key }) => {
        return (
          <CollapsibleListSection key={key} className={style['mappings-collapsible-list']} open={expandKey === key}>
            <CollapsibleListSectionHeader onClick={() => setExpandKey(key)}>{fieldTitle}</CollapsibleListSectionHeader>
            <CollapsibleListSectionContent>
              <div className={style['map-order-import-table']}>
                <div className={style['map-order-import-table-head']}>
                  <p className={style['table-head-first-column']}>
                    <FormattedMessage defaultMessage='ShipStation Field' description='mapOrderImportModal.sSColumnTitle' id='/jkus1' />
                  </p>
                  <p className={style['table-head-second-column']}>
                    <FormattedMessage
                      defaultMessage='Your Column Header'
                      description='mapOrderImportModal.importedColumnTitle'
                      id='LxOyhw'
                    />
                  </p>
                </div>
                {/* Table Content Starts Here */}
                {cols.map(({ name, isRequired }, index) => (
                  <div key={index} className={style['map-order-import-table-content']}>
                    <p className={style['table-content-first-column']}>
                      {getFieldName(name)}
                      <span className={style['inner-text-lighter']}>{isRequired ? <FormattedMessage {...messages.required} /> : null}</span>
                    </p>
                    <div className={style['mappings-dropdown-wrapper']}>
                      <MappingsDropDown
                        getMatchingCSVField={getMatchingCSVField}
                        importedCols={displayCols}
                        onClick={trackClick}
                        setMatchingCSVField={setMatchingCSVField}
                        ssField={name}
                      />
                    </div>
                  </div>
                ))}
                {/* Table Content Ends here */}
              </div>
            </CollapsibleListSectionContent>
          </CollapsibleListSection>
        );
      })}
    </CollapsibleList>
  );
};

OrderImportColumnMapper.propTypes = {
  importedCols: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      mappedToField: PropTypes.string,
    }),
  ),
  shipStationColsGroupings: PropTypes.arrayOf(
    PropTypes.shape({
      fieldTitle: PropTypes.string,
      cols: PropTypes.array,
      key: PropTypes.string,
    }),
  ),
  expandKey: PropTypes.string.isRequired,
  setMatchingCSVField: PropTypes.func.isRequired,
  getMatchingCSVField: PropTypes.func.isRequired,
  setExpandKey: PropTypes.func.isRequired,
};

export default OrderImportColumnMapper;
